import { useEffect, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useAuth } from '@/context/auth'

const primaryLinks = [
  { to: '/about', label: 'About' },
  { to: '/features', label: 'Features' },
  { to: '/contact', label: 'Contact' },
]

const secondaryLinks = [
  { to: '/awa', label: 'AI Workflow Automation' },
  { to: '/for-agents', label: 'For USVI Agents' },
  { to: '/security', label: 'Security' },
  { to: '/help', label: 'Help & FAQ' },
  { to: '/accessibility', label: 'Accessibility' },
]

export function PublicHeader() {
  const [open, setOpen] = useState(false)
  const { session } = useAuth()
  const closeMenu = () => setOpen(false)

  // Lock body scroll when the mobile sheet is open.
  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = open ? 'hidden' : original
    return () => {
      document.body.style.overflow = original
    }
  }, [open])

  return (
    <header className="bg-primary text-white relative z-40">
      <div className="h-1 bg-vice-sunset" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            className="group flex items-center gap-3 sm:gap-4 rounded focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-4 min-w-0"
            aria-label="VICE — Virgin Islands Commercial Environment"
          >
            <img src="/logo-invert.png" alt="VICE" className="h-10 sm:h-11 w-auto flex-shrink-0" />
            <span className="hidden md:inline text-sm font-semibold text-neutral-100 leading-snug max-w-[18ch] border-l border-white/20 pl-4">
              Virgin Islands Commercial Environment
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden md:flex items-center gap-1">
            {primaryLinks.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded text-sm font-medium hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 ${
                    isActive ? 'bg-white/10 text-white' : 'text-neutral-200'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
            <div className="w-px h-6 bg-white/20 mx-2" aria-hidden="true" />
            {session ? (
              <Link
                to="/dashboard"
                className="px-4 py-2 rounded bg-accent-sunset text-primary font-semibold text-sm hover:bg-accent-sunset/90 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
              >
                Go to dashboard
              </Link>
            ) : (
              <Link
                to="/signin"
                className="px-4 py-2 rounded bg-accent-sunset text-primary font-semibold text-sm hover:bg-accent-sunset/90 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
              >
                Sign in
              </Link>
            )}
          </nav>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center p-2 rounded text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          id="mobile-menu"
          className="md:hidden absolute inset-x-0 top-full bg-primary border-t border-white/10 shadow-2xl max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          <nav
            aria-label="Mobile"
            className="px-4 sm:px-6 py-6 space-y-6"
            onClick={(e) => {
              // Close the drawer when any anchor inside is activated.
              const target = e.target as HTMLElement
              if (target.closest('a')) closeMenu()
            }}
          >
            <div>
              <ul className="space-y-1">
                {primaryLinks.map(({ to, label }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      className={({ isActive }) =>
                        `block px-3 py-3 rounded text-base font-semibold hover:bg-white/10 ${
                          isActive ? 'bg-white/10 text-white' : 'text-neutral-100'
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 px-3 mb-2">
                More
              </h2>
              <ul className="space-y-1">
                {secondaryLinks.map(({ to, label }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      className={({ isActive }) =>
                        `block px-3 py-2.5 rounded text-sm hover:bg-white/10 ${
                          isActive ? 'bg-white/10 text-white' : 'text-neutral-300'
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-2 border-t border-white/10">
              {session ? (
                <Link
                  to="/dashboard"
                  className="block w-full px-4 py-3 rounded bg-accent-sunset text-primary font-bold text-center"
                >
                  Go to dashboard
                </Link>
              ) : (
                <Link
                  to="/signin"
                  className="block w-full px-4 py-3 rounded bg-accent-sunset text-primary font-bold text-center"
                >
                  Sign in
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
